import React, { useEffect, useState, useRef } from 'react';
import { Table, Button, Tag, message, Input, Space, Tooltip, Popconfirm } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, LinkOutlined, CheckOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import type { TablePaginationConfig, SorterResult, FilterDropdownProps } from 'antd/es/table/interface';
import type { InputRef } from 'antd';
import { useNavigate } from 'react-router-dom';
import { watchesAPI } from '../../Utils/api';
import { convertFilterSettingsToQuery } from '../../Utils/filterHelpers';
import { IFilterSettings } from '../../Types/FilterTypes';
import { copyText } from '../../Utils/clipboard';
import { useIsMobile } from '../../Utils/useIsMobile';
import { downloadCsv } from '../../Utils/downloadCsv';
import ProductMobileList from '../Components/ProductMobileList/ProductMobileList';

interface Watch {
  id: string;
  brand: string;
  model: string;
  priceEur: string;
  status: string;
  referenceNumber: string;
  sku: string | null;
  slug: string;
  shortCode: string | null;
}

const FE_URL = process.env.REACT_APP_FE_URL || 'https://www.mr-luxury-watches.com';

const ProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const [watches, setWatches] = useState<Watch[]>([]);
  const [loading, setLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Filter & Pagination State
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  const searchInputRef = useRef<InputRef>(null);
  const isMobile = useIsMobile();

  // The mobile list appends pages, so it always starts from the first one
  useEffect(() => {
    setCurrentPage(1);
  }, [isMobile]);

  useEffect(() => {
    fetchWatches();
  }, [currentPage, pageSize, filters, sortField, sortOrder, isMobile]);

  const fetchWatches = async () => {
    setLoading(true);
    try {
      const filterSettings: IFilterSettings = {
        pagination: {
          page: currentPage,
          take: isMobile ? 20 : pageSize,
        },
        sorting: {
          field: sortField,
          order: sortOrder,
        },
      };

      // Add filters
      if (Object.keys(filters).length > 0) {
        filterSettings.filter = Object.entries(filters).map(([field, value]) => ({
          field,
          value,
        }));
      }

      const queryParams = convertFilterSettingsToQuery(filterSettings);
      const response = await watchesAPI.getAllAdmin(queryParams);
      const list = response.data.watches || [];
      setWatches((prev) => (isMobile && currentPage > 1 ? [...prev, ...list] : list));
      setTotalCount(response.data.meta?.count || 0);
    } catch (error) {
      message.error('Fehler beim Laden der Produkte');
    } finally {
      setLoading(false);
    }
  };

  const [exporting, setExporting] = useState(false);

  // Exports every product matching the current filters and sorting, not just the visible page
  const exportCsv = async () => {
    setExporting(true);
    try {
      const query = convertFilterSettingsToQuery({
        sorting: { field: sortField, order: sortOrder },
        filter: Object.entries(filters).map(([field, value]) => ({ field, value })),
      });
      const response = await watchesAPI.exportAdmin(query);
      downloadCsv(response.data.csv, response.data.filename);
    } catch (error) {
      message.error('Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };
  const exportButton = (
    <Button icon={<DownloadOutlined />} onClick={exportCsv} loading={exporting} aria-label="CSV-Export" data-testid="products-export">
      {isMobile ? null : 'CSV-Export'}
    </Button>
  );

  const handleTableChange = (
    pagination: TablePaginationConfig,
    tableFilters: any,
    sorter: SorterResult<Watch> | SorterResult<Watch>[]
  ) => {
    // Handle pagination
    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
    if (pagination.pageSize) {
      setPageSize(pagination.pageSize);
    }

    const status = tableFilters?.status?.[0];
    if ((status || '') !== (filters.status || '')) {
      handleFilter('status', status || '');
    }

    // Handle sorting
    if (!Array.isArray(sorter) && sorter.field) {
      const field = Array.isArray(sorter.field) ? sorter.field[0] : sorter.field;
      setSortField(String(field));
      setSortOrder(sorter.order === 'ascend' ? 'ASC' : 'DESC');
    }
  };

  const handleFilter = (field: string, value: string) => {
    const newFilters = { ...filters };
    if (value) {
      newFilters[field] = value;
    } else {
      delete newFilters[field];
    }
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const getColumnSearchProps = (dataIndex: string, title: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInputRef}
          placeholder={`${title} durchsuchen`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm();
            handleFilter(dataIndex, selectedKeys[0] as string);
          }}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              handleFilter(dataIndex, selectedKeys[0] as string);
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Suchen
          </Button>
          <Button
            onClick={() => {
              if (clearFilters) {
                clearFilters();
              }
              handleFilter(dataIndex, '');
            }}
            size="small"
            style={{ width: 90 }}
          >
            Zurücksetzen
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    filteredValue: filters[dataIndex] ? [filters[dataIndex]] : null,
  });

  const columns = [
    {
      title: 'Marke',
      dataIndex: 'brand',
      key: 'brand',
      sorter: true,
      ...getColumnSearchProps('brand', 'Marke'),
    },
    {
      title: 'Modell',
      dataIndex: 'model',
      key: 'model',
      sorter: true,
      ...getColumnSearchProps('model', 'Modell'),
    },
    {
      title: 'Preis',
      dataIndex: 'priceCents',
      key: 'priceEur',
      sorter: true,
      render: (priceCents: number) => {
        if (!priceCents && priceCents !== 0) return '€0,00';
        const priceEur = priceCents / 100;
        return `€${priceEur.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Verfügbar', value: 'AVAILABLE' },
        { text: 'Reserviert', value: 'RESERVED' },
        { text: 'Entwurf', value: 'DRAFT' },
        { text: 'Verkauft', value: 'SOLD' },
      ],
      filterMultiple: false,
      filteredValue: filters.status ? [filters.status] : null,
      render: (status: string) => (
        <Tag color={
          status === 'AVAILABLE' ? 'green'
          : status === 'RESERVED' ? 'orange'
          : status === 'DRAFT' || status === 'ARCHIVED' ? 'default'
          : 'red'
        }>
          {status === 'AVAILABLE' ? 'Verfügbar'
            : status === 'RESERVED' ? 'Reserviert'
            : status === 'DRAFT' ? 'Entwurf'
            : status === 'ARCHIVED' ? 'Archiviert'
            : 'Verkauft'}
        </Tag>
      ),
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      sorter: true,
      ...getColumnSearchProps('sku', 'SKU'),
      render: (sku: string | null) => (sku ? <span style={{ fontFamily: 'monospace' }}>{sku}</span> : <Tag>—</Tag>),
    },
    {
      title: 'Referenznummer',
      dataIndex: 'referenceNumber',
      key: 'referenceNumber',
      ...getColumnSearchProps('referenceNumber', 'Referenznummer'),
    },
    {
      title: 'Short-Link',
      dataIndex: 'shortCode',
      key: 'shortCode',
      render: (shortCode: string | null) => {
        if (!shortCode) return <Tag>—</Tag>;
        const url = `${FE_URL}/s/${shortCode}`;
        const copy = async (e: React.MouseEvent) => {
          e.stopPropagation();
          if (await copyText(url)) {
            setCopiedCode(shortCode);
            setTimeout(() => setCopiedCode((c) => (c === shortCode ? null : c)), 2000);
          }
        };
        return (
          <Space size={4}>
            <a href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ fontFamily: 'monospace' }}>
              /s/{shortCode}
            </a>
            <Tooltip title={copiedCode === shortCode ? 'Kopiert' : 'Link kopieren'}>
              <Button
                size="small"
                type="text"
                icon={copiedCode === shortCode ? <CheckOutlined style={{ color: '#52c41a' }} /> : <LinkOutlined />}
                onClick={copy}
                data-testid={`copy-short-${shortCode}`}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (_: any, record: Watch) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/products/${record.id}`)}
            data-testid={`edit-watch-${record.id}`}
          >
            Bearbeiten
          </Button>
          <Popconfirm
            title="Produkt löschen"
            description={`Soll "${record.brand} ${record.model}" wirklich gelöscht werden?`}
            okText="Ja, löschen"
            cancelText="Abbrechen"
            okButtonProps={{ danger: true }}
            onConfirm={async () => {
              try {
                await watchesAPI.deleteWatch(record.id);
                message.success('Produkt gelöscht');
                fetchWatches();
              } catch (e: any) {
                message.error(e?.response?.data?.message || 'Löschen fehlgeschlagen');
              }
            }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              data-testid={`delete-watch-${record.id}`}
            >
              Löschen
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (isMobile) {
    return (
      <ProductMobileList
        watches={watches as any}
        totalCount={totalCount}
        loading={loading}
        onLoadMore={() => setCurrentPage((page) => page + 1)}
        onDeleted={(id) => {
          setWatches((prev) => prev.filter((w) => w.id !== id));
          setTotalCount((count) => count - 1);
        }}
        filters={filters}
        onFiltersChange={(next) => { setFilters(next); setCurrentPage(1); }}
        sortField={sortField}
        sortOrder={sortOrder}
        onSortChange={(field, order) => { setSortField(field); setSortOrder(order); setCurrentPage(1); }}
        exportButton={exportButton}
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1>Produkte</h1>
        <Space>
          {exportButton}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`/products/new`)}>
            Neues Produkt
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={watches}
        rowKey="id"
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          showTotal: (total) => `Gesamt: ${total} Produkte`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
      />
    </div>
  );
};

export default ProductsPage;
